import {
  useState, useEffect, axios, Button, Input, Card, CardContent, CardHeader, CardTitle,
  Label, Badge, Loader2, Plus, Trash2, API, useAuth, toast,
} from "@/pages/shared";
import { Search, RefreshCw, Target as TargetIcon, ChevronDown, ChevronUp, AlertCircle, Lightbulb, TrendingUp, TrendingDown, Star, MinusCircle, CheckCircle2, Circle } from "lucide-react";

// Compact run-over-run delta badge — surfaces momentum on the query card.
const TrendBadge = ({ trend }) => {
  if (!trend || trend.kind === "first") return null;
  const map = {
    improved: { Icon: TrendingUp, label: `↑ #${trend.prev_position} → #${trend.new_position}`, cls: "bg-green-600/20 text-green-300 border-green-600/40" },
    declined: { Icon: TrendingDown, label: `↓ #${trend.prev_position} → #${trend.new_position}`, cls: "bg-violet-600/20 text-violet-300 border-violet-600/40" },
    newly_cited: { Icon: Star, label: `Newly cited at #${trend.new_position}`, cls: "bg-amber-500/20 text-amber-200 border-amber-500/40" },
    dropped: { Icon: MinusCircle, label: `Dropped from answer (was #${trend.prev_position})`, cls: "bg-violet-600/20 text-violet-300 border-violet-600/40" },
    still_missing: { Icon: MinusCircle, label: "Still not cited", cls: "bg-white/5 text-muted-foreground border-white/10" },
    unchanged: { Icon: TargetIcon, label: `Unchanged at #${trend.new_position}`, cls: "bg-white/5 text-muted-foreground border-white/10" },
  };
  const m = map[trend.kind];
  if (!m) return null;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${m.cls}`} data-testid="trend-badge">
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

export default function CitationsPage() {
  const { user } = useAuth();
  const [limits, setLimits] = useState({ plan: "free", limit: 0, used: 0, remaining: 0 });
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState({});
  const [planning, setPlanning] = useState({});
  const [plans, setPlans] = useState({});
  const [expanded, setExpanded] = useState({});
  const [history, setHistory] = useState({});

  // Form state
  const [queryText, setQueryText] = useState("");
  const [domains, setDomains] = useState("");

  const refresh = async () => {
    try {
      const [lim, lst] = await Promise.all([
        axios.get(`${API}/citations/limits`),
        axios.get(`${API}/citations/queries`),
      ]);
      setLimits(lim.data);
      setQueries(lst.data.queries || []);
    } catch (e) {
      toast.error("Could not load tracked queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const createQuery = async () => {
    if (!queryText.trim()) { toast.error("Add a query"); return; }
    const targets = domains.split(",").map((d) => d.trim()).filter(Boolean);
    if (!targets.length) { toast.error("Add at least one target domain"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/citations/queries`, { query_text: queryText.trim(), target_domains: targets });
      toast.success("Query added");
      setQueryText(""); setDomains("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add query");
    }
    setCreating(false);
  };

  const runQuery = async (id) => {
    setRunning((r) => ({ ...r, [id]: true }));
    try {
      await axios.post(`${API}/citations/queries/${id}/run`);
      toast.success("Citation snapshot updated");
      // Auto-expand the latest snapshot so the AI answer + competitor chips are immediately visible
      setExpanded((e) => ({ ...e, [id]: true }));
      await Promise.all([refresh(), loadHistory(id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    }
    setRunning((r) => ({ ...r, [id]: false }));
  };

  const deleteQuery = async (id) => {
    if (!window.confirm("Delete this tracked query and its history?")) return;
    try {
      await axios.delete(`${API}/citations/queries/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch { toast.error("Delete failed"); }
  };

  const loadHistory = async (id) => {
    try {
      const r = await axios.get(`${API}/citations/queries/${id}/snapshots`);
      setHistory((h) => ({ ...h, [id]: r.data.snapshots || [] }));
    } catch { /* ignore */ }
  };

  const toggleExpand = async (id) => {
    const isOpen = !!expanded[id];
    setExpanded((e) => ({ ...e, [id]: !isOpen }));
    if (!isOpen && !history[id]) await loadHistory(id);
    if (!isOpen && !plans[id]) await loadPlan(id);
  };

  const loadPlan = async (id) => {
    try {
      const r = await axios.get(`${API}/citations/queries/${id}/action-plan`);
      setPlans((p) => ({ ...p, [id]: r.data.plan || null }));
    } catch { /* ignore */ }
  };

  const generatePlan = async (id) => {
    setPlanning((p) => ({ ...p, [id]: true }));
    try {
      const r = await axios.post(`${API}/citations/queries/${id}/action-plan`);
      setPlans((p) => ({ ...p, [id]: r.data.plan }));
      setExpanded((e) => ({ ...e, [id]: true }));
      toast.success("Action plan ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate plan");
    }
    setPlanning((p) => ({ ...p, [id]: false }));
  };

  const toggleTactic = async (queryId, index, currentlyDone) => {
    // Optimistic UI update first
    setPlans((p) => {
      const plan = p[queryId];
      if (!plan?.tactics) return p;
      const next = { ...plan, tactics: plan.tactics.map((t, i) =>
        i === index ? { ...t, done: !currentlyDone, done_at: !currentlyDone ? new Date().toISOString() : null } : t
      ) };
      return { ...p, [queryId]: next };
    });
    try {
      await axios.patch(`${API}/citations/queries/${queryId}/action-plan/tactic/${index}`, { done: !currentlyDone });
    } catch (e) {
      toast.error("Could not update tactic");
      // Roll back on failure
      setPlans((p) => {
        const plan = p[queryId];
        if (!plan?.tactics) return p;
        const next = { ...plan, tactics: plan.tactics.map((t, i) =>
          i === index ? { ...t, done: currentlyDone } : t
        ) };
        return { ...p, [queryId]: next };
      });
    }
  };

  const planUpsell = limits.limit === 0;

  return (
    <div className="space-y-8" data-testid="citations-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Search className="w-7 h-7 text-violet-500" /> AI Search Citation Tracker
        </h1>
        <p className="text-muted-foreground max-w-3xl">
          Track which queries an AI answer engine (ChatGPT Search, Perplexity, Gemini) would mention your brand for.
          Add a target query + your domain, then run a snapshot to see if you're cited and where you rank.
        </p>
      </div>

      {/* Plan / quota */}
      <Card className="glass">
        <CardContent className="pt-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" data-testid="citations-plan-badge">{limits.plan?.toUpperCase()} plan</Badge>
            <span className="text-sm text-muted-foreground">
              {limits.used} / {limits.limit === 0 ? "—" : limits.limit} tracked queries
            </span>
          </div>
          {planUpsell && (
            <a href="/dashboard/settings" className="text-sm font-semibold text-violet-400 hover:underline" data-testid="citations-upgrade-link">
              Upgrade to Pro to unlock citation tracking →
            </a>
          )}
        </CardContent>
      </Card>

      {/* Add query */}
      {!planUpsell && (
        <Card className="glass">
          <CardHeader><CardTitle>Track a new query</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>SEO query</Label>
                <Input
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  placeholder="best AI content tools for affiliate marketing"
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="citations-query-input"
                />
              </div>
              <div>
                <Label>Your domain(s) — comma separated</Label>
                <Input
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  placeholder="creatara.ai, mybrand.com"
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="citations-domain-input"
                />
              </div>
            </div>
            <Button
              onClick={createQuery}
              disabled={creating || limits.remaining <= 0}
              className="mt-4 btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full"
              data-testid="citations-add-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add tracked query
            </Button>
            {limits.remaining <= 0 && limits.limit > 0 && (
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Plan limit reached. Delete an existing query or upgrade.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Queries */}
      <Card className="glass">
        <CardHeader><CardTitle>Tracked queries</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          ) : queries.length === 0 ? (
            <p className="text-muted-foreground text-center py-8" data-testid="citations-empty">
              No queries yet. {planUpsell ? "Upgrade to Pro to start tracking AI-search citations." : "Add your first query above."}
            </p>
          ) : (
            <div className="space-y-3" data-testid="citations-list">
              {queries.map((q) => {
                const latest = q.latest;
                const matched = latest?.matched_targets || [];
                const isMatched = matched.length > 0;
                return (
                  <div key={q.id} className="rounded-lg bg-white/5 border border-white/10" data-testid={`citation-query-${q.id}`}>
                    <div className="p-4 flex flex-wrap items-start gap-4 justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium break-words">{q.query_text}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {q.target_domains.map((d) => (
                            <Badge key={d} variant="secondary" className="font-mono text-xs">{d}</Badge>
                          ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                          {latest ? (
                            <>
                              <span className={`flex items-center gap-1 font-semibold ${isMatched ? "text-green-400" : "text-amber-400"}`}>
                                <TargetIcon className="w-4 h-4" />
                                {isMatched ? `Cited (#${matched[0].position})` : "Not cited"}
                              </span>
                              <TrendBadge trend={latest.trend} />
                              <span className="text-muted-foreground">{latest.cited_total} domains in answer</span>
                              <span className="text-muted-foreground text-xs">
                                Last run: {new Date(latest.run_at).toLocaleString()}
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground text-sm">Never run yet</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runQuery(q.id)}
                          disabled={!!running[q.id]}
                          data-testid={`citation-run-${q.id}`}
                        >
                          {running[q.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          <span className="ml-1">Run</span>
                        </Button>
                        {latest && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => generatePlan(q.id)}
                            disabled={!!planning[q.id]}
                            className="border-amber-600/40 text-amber-300 hover:bg-amber-600/10"
                            data-testid={`citation-plan-${q.id}`}
                            title="Generate a 'Get Cited' action plan"
                          >
                            {planning[q.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
                            <span className="ml-1 hidden sm:inline">Get Cited</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleExpand(q.id)}
                          data-testid={`citation-expand-${q.id}`}
                        >
                          {expanded[q.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteQuery(q.id)}
                          data-testid={`citation-delete-${q.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-violet-400" />
                        </Button>
                      </div>
                    </div>

                    {expanded[q.id] && (
                      <div className="border-t border-white/10 p-4 space-y-4 bg-black/20">
                        {/* Action Plan card — at the top of expanded section so users see actionable guidance first */}
                        {plans[q.id] && plans[q.id].tactics?.length > 0 && (() => {
                          const tactics = plans[q.id].tactics;
                          // Stable original index for backend PATCH calls, then sort done→bottom for display
                          const indexed = tactics.map((t, i) => ({ ...t, _orig: i }));
                          const sorted = [
                            ...indexed.filter((t) => !t.done),
                            ...indexed.filter((t) => t.done),
                          ];
                          const doneCount = tactics.filter((t) => t.done).length;
                          const total = tactics.length;
                          const pct = Math.round((doneCount / total) * 100);
                          return (
                          <div className="rounded-lg p-4 bg-amber-500/5 border border-amber-500/20" data-testid={`citation-plan-card-${q.id}`}>
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                              <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                                <Lightbulb className="w-4 h-4" /> Get Cited — Action Plan
                              </p>
                              <span className="text-xs text-muted-foreground">
                                {doneCount} / {total} complete · {new Date(plans[q.id].created_at).toLocaleDateString()}
                              </span>
                            </div>
                            {/* Progress bar */}
                            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-4" data-testid={`citation-plan-progress-${q.id}`}>
                              <div
                                className="h-full bg-amber-400 transition-all duration-300"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <div className="space-y-3">
                              {sorted.map((t) => (
                                <div
                                  key={t._orig}
                                  className={`rounded p-3 border transition-opacity ${t.done ? "bg-black/40 border-green-600/20 opacity-60" : "bg-black/30 border-white/5"}`}
                                  data-testid={`tactic-${q.id}-${t._orig}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <button
                                      onClick={() => toggleTactic(q.id, t._orig, !!t.done)}
                                      className="mt-0.5 flex-shrink-0 hover:scale-110 transition-transform"
                                      title={t.done ? "Mark as not done" : "Mark as done"}
                                      data-testid={`tactic-toggle-${q.id}-${t._orig}`}
                                    >
                                      {t.done ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                                      ) : (
                                        <Circle className="w-5 h-5 text-muted-foreground hover:text-amber-300" />
                                      )}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="text-xs font-mono text-amber-400">#{t._orig + 1}</span>
                                        <p className={`font-semibold text-sm flex-1 min-w-0 ${t.done ? "line-through" : ""}`}>{t.title}</p>
                                        {t.effort && <Badge variant="secondary" className="text-[10px] uppercase">Effort: {t.effort}</Badge>}
                                        {t.impact && <Badge variant="secondary" className="text-[10px] uppercase bg-green-600/20 text-green-300">Impact: {t.impact}</Badge>}
                                      </div>
                                      <p className="text-sm text-muted-foreground mb-1">{t.description}</p>
                                      {t.first_step && !t.done && (
                                        <p className="text-xs text-amber-200/80"><span className="font-semibold">First step:</span> {t.first_step}</p>
                                      )}
                                      {t.done && t.done_at && (
                                        <p className="text-xs text-green-400/80">Completed {new Date(t.done_at).toLocaleDateString()}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          );
                        })()}

                        {(history[q.id] || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground">No snapshots yet. Click Run.</p>
                        ) : (
                          history[q.id].map((s) => {
                            const targetSet = (q.target_domains || []);
                            const competitors = (s.all_mentions || []).filter(
                              (m) => !targetSet.some((t) => m === t || m.endsWith("." + t))
                            );
                            return (
                            <div key={s.id} className="space-y-3 border-l-2 border-violet-500/40 pl-3" data-testid={`citation-snapshot-${s.id}`}>
                              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                <span>{new Date(s.run_at).toLocaleString()}</span>
                                <span>·</span>
                                <span>{s.cited_total} citations</span>
                                {s.engine === "gemini-grounded" ? (
                                  <Badge className="bg-blue-600/30 text-blue-200 border border-blue-500/40" title={s.model}>
                                    🌐 Gemini · live web
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-white/5 text-muted-foreground" title={s.model}>
                                    GPT · training data
                                  </Badge>
                                )}
                                {s.matched_targets?.length > 0 ? (
                                  <Badge className="bg-green-600">
                                    matched: {s.matched_targets.map((m) => `${m.domain} (#${m.position})`).join(", ")}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-amber-600/30 text-amber-200">Your domain not in answer</Badge>
                                )}
                              </div>

                              {/* Search queries Google actually used (grounding only) */}
                              {s.search_queries?.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  <span className="font-semibold">Google ran: </span>
                                  {s.search_queries.map((q, i) => (
                                    <span key={i} className="font-mono mr-2">"{q}"</span>
                                  ))}
                                </div>
                              )}

                              {/* Always-visible ranked citation map — clickable when we have grounding source URIs */}
                              {s.all_mentions?.length > 0 && (
                                <div data-testid={`citation-mentions-${s.id}`}>
                                  <p className="text-xs font-semibold text-muted-foreground mb-1.5">
                                    Citation map (in order)
                                    {s.engine === "gemini-grounded" && <span className="text-blue-300 font-normal ml-1">— click any to open the actual source</span>}
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {s.all_mentions.map((m, idx) => {
                                      const isTarget = targetSet.some((t) => m === t || m.endsWith("." + t));
                                      // Match the grounding_source by domain to get a clickable URI
                                      const src = (s.grounding_sources || []).find((g) => g.domain === m);
                                      const cls = `text-xs font-mono px-2 py-0.5 rounded ${isTarget ? "bg-green-600/30 border border-green-500/50 text-green-200" : "bg-white/5 border border-white/10"} ${src?.uri ? "hover:border-blue-400/60 hover:bg-blue-500/10 cursor-pointer transition-colors" : ""}`;
                                      const label = `${idx + 1}. ${m}`;
                                      return src?.uri ? (
                                        <a
                                          key={`${m}-${idx}`}
                                          href={src.uri}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={cls}
                                          title={`Open: ${src.title}`}
                                        >
                                          {label}
                                        </a>
                                      ) : (
                                        <span key={`${m}-${idx}`} className={cls}>{label}</span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Top competitors summary — quick scan */}
                              {competitors.length > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="text-amber-300 font-semibold">Top competitors cited: </span>
                                  {competitors.slice(0, 5).join(", ")}{competitors.length > 5 ? "…" : ""}
                                </p>
                              )}

                              {/* AI answer — open by default so the user reads it without an extra click */}
                              <details className="text-sm" open>
                                <summary className="cursor-pointer text-muted-foreground hover:text-white text-xs font-semibold">View AI answer ({s.model || s.engine || "model unknown"})</summary>
                                <p className="whitespace-pre-wrap mt-2 text-sm leading-relaxed bg-black/30 p-3 rounded">{s.response_text}</p>
                              </details>
                            </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
